import React from "react";
import { AbsoluteFill, Easing, Img, interpolate, spring, useCurrentFrame, useVideoConfig } from "remotion";

import AppStore from "components/website/img/apple-appstore.svg";
import PlayStore from "components/website/img/playstore.svg";
import StipplMarkWhite from "./stippl-mark-white.svg";
import { mediaUrl } from "../sequences/reelMedia";
import StipplWordmark from "./stippl-wordmark.svg";
// The control logo Mapbox ships is built for sitting ON a map: a white fill with a black 3px
// outline behind it at 30% opacity. On a white card the fill vanishes and only that soft outline is
// left, which reads as a blurry logo rather than as the wrong asset. Same paths, filled solid.
import MapboxLogo from "./mapbox-logo-dark.svg";
import { routeOutline } from "./routeOutline";
import countryCodes from "components/dashboard/countryISOCodes.json";
import { COUNTRY_NAME_ALIASES } from "utils/countryNameAliases";

/**
 * The closing card, as a composition rather than a video file.
 *
 * It replaces `stipplOutro.webm`. The clip was 3.50 s while `OUTRO_DURATION` was 110 frames
 * (3.67 s), so the last five frames held a frozen picture; there were two files to keep in step
 * (portrait and landscape) plus a third copy under tripmap; the B2B path fell back to a plain black
 * fade because a branded clip would have meant a fourth file; and the hard cut from map to card was
 * unavoidable, because a video that starts after the map cannot dissolve out of it.
 *
 * Timing measured off the reference: the map dissolves over the first eleven frames, the mark lands,
 * the wordmark arrives beside it, and the store badges come last. The map credit sits under the
 * lockup the whole time -- Mapbox requires attribution, and a credit that is part of the design
 * reads as a credit rather than as small print.
 */

/** Beats, in frames at 30 fps. */
export const OUTRO_DISSOLVE = 16;
const ROUTE_DRAW = 46;
const MARK_IN = 30;
const GLYPH_IN = 66;
const BORDERS_OUT = 80;
const WORDMARK_IN = 96;
const NAME_IN = 118;
const ATTRIB_IN = 134;
const BADGES_IN = 156;
/** The box the route is drawn in, authored against the 1080x1920 card. */
const ROUTE_W = 620;
const ROUTE_H = 420;

export const REEL_OUTRO_FRAMES = 226;

const BRAND_GREEN = "#13C892";

/** True for a colour too light to read as a line on the card's white ground. */
export const paleOnWhite = (color?: string) => {
	const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec((color || "").trim());
	if (!m) return false;
	const hex = m[1].length === 3 ? m[1].replace(/./g, (c) => c + c) : m[1];
	const n = parseInt(hex, 16);
	const luminance = (0.2126 * ((n >> 16) & 255) + 0.7152 * ((n >> 8) & 255) + 0.0722 * (n & 255)) / 255;
	return luminance > 0.85;
};

const ReelOutro = ({
	mark,
	wordmark,
	brandName,
	routeColor,
	reelData,
	countries,
	tripName,
	stats,
}: {
	mark?: string;
	wordmark?: string;
	brandName?: string;
	routeColor?: string;
	reelData?: any[];
	countries?: { name?: string }[];
	tripName?: string;
	stats?: string;
}) => {
	const frame = useCurrentFrame();
	const { fps, width, height } = useVideoConfig();

	// The card is authored at 1080x1920 and scaled, so one set of numbers serves every output size.
	const scale = Math.min(width / 1080, height / 1920);
	const landscape = width > height;

	// The map is still underneath for these frames; the card dissolves in over the top of it.
	const sheet = interpolate(frame, [0, OUTRO_DISSOLVE], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

	const rise = (at: number, stiffness = 140) =>
		spring({ frame: frame - at, fps, config: { mass: 1, damping: 14, stiffness } });

	// Three squares, each on its own spring. The stiffness is what sets them apart: the softer the
	// spring the longer that square keeps turning, so they visibly separate on the way in and only
	// agree at the end. Turning them as one object reads as a flourish; this reads as assembly.
	const turn = (stiffness: number, damping: number) =>
		spring({ frame: frame - MARK_IN, fps, config: { mass: 1, damping, stiffness } });
	const tile = turn(44, 13);
	const ink = turn(38, 13);
	const haze = turn(33, 14);

	// The mark appears once the spinning has all but stopped, not while it is still turning.
	const glyph = interpolate(frame, [GLYPH_IN, GLYPH_IN + 12], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
	// Then the scaffolding leaves, and what is left is the icon.
	const scaffold = interpolate(frame, [BORDERS_OUT, BORDERS_OUT + 14], [1, 0], {
		extrapolateLeft: "clamp",
		extrapolateRight: "clamp",
		easing: Easing.in(Easing.cubic),
	});

	// One progress value drives both halves of the same move: the icon has been centred on its own
	// until now, so as the word is revealed the pair slides left by half the word's width. Driving
	// them separately is how a lockup ends up off-centre for a few frames.
	const reveal = interpolate(frame, [WORDMARK_IN, WORDMARK_IN + 20], [0, 1], {
		extrapolateLeft: "clamp",
		extrapolateRight: "clamp",
		easing: Easing.out(Easing.cubic),
	});
	/** Half of (word width + gap): what the pair has to move so the lockup ends up centred. */
	const LOCKUP_SHIFT = 162;
	const badgeIn = rise(BADGES_IN, 110);

	const px = (n: number) => `${n * scale}px`;

	// Authored against the same 760x560 box the svg declares, so the projection does not have to know
	// about the output resolution.
	// Landscape has width to spare and no height. With the drawing on top and the signature and the
	// name sharing the line below it, it can take most of the width -- which is the whole reason to
	// lay a wide frame out differently rather than scaling a tall one down into it.
	const routeW = landscape ? ROUTE_W * 1.8 : ROUTE_W;
	const routeH = landscape ? ROUTE_H * 0.8 : ROUTE_H;
	// Dots and stroke shrink as stops multiply. A four-stop crawl wants big markers; a twenty-stop
	// tour with the same box would turn them into one continuous blob, so the drawing has to give way
	// to the route as the route gets busier. The count does not depend on the box, so a first pass at
	// full size gives it.
	const stopCount = routeOutline(reelData || [], routeW, routeH, 36).stops.length;
	const dotR = Math.max(5, Math.min(12, 13 - stopCount * 0.55));
	// Cards shrink with the route too, for the same reason the dots do.
	const cardW = Math.max(44, Math.min(78, 86 - stopCount * 3.4));
	const cardH = cardW * 1.28;
	const strokeW = Math.max(3.5, Math.min(6, 7 - stopCount * 0.14));
	// A card hangs above its stop. Fitted to the full box, a stop on the top edge hung its card out of
	// the box and over the trip's name and stats above it (a Bangkok day trip covered "5 stops"). Fit
	// the route into the box less a card's height and move it down by that much, so every card stays
	// inside; the box keeps its size, so nothing else on the card moves.
	const cardRoom = (reelData || []).some((d: any) => d?.photos?.length) ? cardH + dotR + 8 : 0;
	const fitted = routeOutline(reelData || [], routeW, routeH - cardRoom, 36);
	const outline = { ...fitted, stops: fitted.stops.map((s) => ({ ...s, y: s.y + cardRoom })) };
	const draw = interpolate(frame, [0, ROUTE_DRAW], [0, 1], {
		extrapolateLeft: "clamp",
		extrapolateRight: "clamp",
		easing: Easing.inOut(Easing.cubic),
	});
	const dots = interpolate(frame, [ROUTE_DRAW - 8, ROUTE_DRAW + 10], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
	const nameIn = interpolate(frame, [NAME_IN, NAME_IN + 14], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

	// Same lookup the rest of the app uses, so a country that resolves in a chip resolves here too.
	// flagcdn rather than react-country-flag: that renders a bare <img>, which Remotion does not wait
	// for, so a flag that had not arrived yet would simply be missing from the frame.
	const flags = (countries || [])
		.map((c) => {
			const name = c?.name ? (COUNTRY_NAME_ALIASES[c.name] ?? c.name) : undefined;
			return (countryCodes as any[]).find((row) => row.country === name)?.alpha2?.toLowerCase();
		})
		.filter(Boolean) as string[];

	// The lockup is the signature, so in landscape it goes under the pair rather than beside the
	// route -- and larger, because a wide frame gives it the room a tall one does not.
	// A partner's card carries their colour and their logo, and drops the store buttons: those send
	// the viewer to our app, which is not what a partner is presenting.
	// The route takes the reel's transport colour, which suits the map it was chosen for and can be
	// white on a dark one -- and white on this white card is no route at all. Too pale, and the
	// drawing falls back to the brand green.
	const line = routeColor && !paleOnWhite(routeColor) ? routeColor : BRAND_GREEN;
	const partner = Boolean(brandName);
	const lock = landscape ? 1.35 : 1;
	const lx = (n: number) => `${n * scale * lock}px`;

	const routeBlock = outline.legs.length ? (
		/* Route and its photo cards live in one box, so placing them is a single decision. */
		<div style={{ position: "relative", width: px(routeW), height: px(routeH), flexShrink: 0 }}>
			<svg width={px(routeW)} height={px(routeH)} viewBox={`0 0 ${routeW} ${routeH}`} style={{ position: "absolute", inset: 0, overflow: "visible" }}>
				<g transform={`translate(0 ${cardRoom})`}>
					{outline.legs.map((d, i) => (
						<path
							key={i}
							d={d}
							fill="none"
							stroke={line}
							strokeWidth={strokeW}
							strokeLinecap="round"
							strokeLinejoin="round"
							opacity={0.72}
							pathLength={1}
							strokeDasharray={1}
							strokeDashoffset={1 - draw}
						/>
					))}
				</g>
				{outline.stops.map((s, i) => (
					<circle key={i} cx={s.x} cy={s.y} r={dotR} fill={line} opacity={0.9 * dots} />
				))}
				{outline.stops.map((s, i) => (
					<circle key={`h${i}`} cx={s.x} cy={s.y} r={dotR * 2} fill={line} opacity={0.18 * dots} />
				))}
			</svg>

			{/* A photo pinned to each stop, alternating which way it leans -- the same trick the map
			    fans use, so the card and the film feel like one thing. Only where a destination has a
			    picture: an empty frame on a stop reads worse than no frame at all. */}
			{outline.stops.map((s, i) => {
				const photo = reelData?.[s.dest]?.photos?.[0];
				// Through the same resolver PhotoSequence uses. A photo uploaded from the app is stored as a
				// bare media.stippl.io key, and handed to <Img> as-is it resolves against the bundle's own
				// origin: two production renders failed that way on 2026-09-20, the day this card shipped.
				const src = photo ? mediaUrl(photo) : "";
				if (!src) return null;
				const pop = spring({ frame: frame - (ROUTE_DRAW - 6 + i * 4), fps, config: { mass: 1, damping: 12, stiffness: 150 } });
				return (
					<div
						key={`p${i}`}
						style={{
							position: "absolute",
							top: px(s.y - cardH - dotR - 8),
							left: px(s.x),
							width: px(cardW),
							height: px(cardH),
							marginLeft: px(-cardW / 2),
							borderRadius: px(9),
							overflow: "hidden",
							backgroundColor: "#FFFFFF",
							border: `${px(3)} solid #FFFFFF`,
							boxShadow: `0 ${px(3)} ${px(10)} rgba(15,62,74,0.22)`,
							transform: `rotate(${i % 2 === 0 ? -7 : 7}deg) scale(${interpolate(pop, [0, 1], [0.3, 1])})`,
							opacity: interpolate(pop, [0, 1], [0, 1], { extrapolateRight: "clamp" }),
						}}
					>
						<Img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: px(6) }} />
					</div>
				);
			})}
		</div>
	) : null;

	// Three squares, not one. Shot by shot the reference grows a filled tile and an outlined one out
	// of a single dot, spins them at different rates so they visibly come apart, then lets them
	// converge and settle upright while the glyph appears inside. Stippl has no rounded app-icon
	// asset -- apple-touch-icon.png is the bare mark on transparency -- so the tile is drawn here.
	// A partner has one logo, not a mark and a wordmark. Drawing our lockup with their file in both
	// slots put their logo on screen twice, inside a Stippl-green tile. Theirs simply arrives.
	const lockup = partner ? (
		<div
			style={{
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				height: lx(200),
				transform: `scale(${interpolate(tile, [0, 1], [0.72, 1])})`,
				opacity: interpolate(frame, [MARK_IN, MARK_IN + 14], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" }),
			}}
		>
			<Img src={wordmark || mark || StipplWordmark} alt="" style={{ height: lx(150), width: "auto" }} />
		</div>
	) : (

		<div
			style={{
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				gap: lx(28),
				height: lx(200),
				transform: `translateX(${interpolate(reveal, [0, 1], [LOCKUP_SHIFT, 0]) * scale * lock}px)`,
			}}
		>
			<div style={{ position: "relative", width: lx(168), height: lx(168), flexShrink: 0 }}>
				<div
					style={{
						position: "absolute",
						inset: 0,
						border: `${lx(3)} solid #C3D0D3`,
						borderRadius: lx(42),
						transform: `rotate(${interpolate(haze, [0, 1], [-585, 0])}deg) scale(${interpolate(haze, [0, 1], [0.04, 1.2])})`,
						opacity: scaffold * interpolate(frame, [MARK_IN, MARK_IN + 4], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" }),
					}}
				/>
				<div
					style={{
						position: "absolute",
						inset: 0,
						border: `${lx(4)} solid #0F3E4A`,
						borderRadius: lx(40),
						transform: `rotate(${interpolate(ink, [0, 1], [430, 0])}deg) scale(${interpolate(ink, [0, 1], [0.05, 1.09])})`,
						opacity: scaffold * interpolate(frame, [MARK_IN, MARK_IN + 4], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" }),
					}}
				/>
				<div
					style={{
						position: "absolute",
						inset: 0,
						backgroundColor: "#13C892",
						borderRadius: lx(40),
						transform: `rotate(${interpolate(tile, [0, 1], [-720, 0])}deg) scale(${interpolate(tile, [0, 1], [0.02, 1])})`,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					<Img src={mark || StipplMarkWhite} alt="" style={{ height: lx(96), width: "auto", opacity: glyph }} />
				</div>
			</div>
			<div style={{ clipPath: `inset(0 ${interpolate(reveal, [0, 1], [100, 0])}% 0 0)`, display: "flex" }}>
				<Img src={wordmark || StipplWordmark} alt="" style={{ height: lx(104), width: "auto" }} />
			</div>
		</div>
	);

	const tripInfo = tripName ? (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				gap: px(10),
				opacity: nameIn,
				transform: `translateY(${interpolate(nameIn, [0, 1], [14, 0]) * scale}px)`,
			}}
		>
			{flags.length ? (
				<div style={{ display: "flex", flexDirection: "row", gap: px(12), marginBottom: px(6) }}>
					{flags.map((code, i) => (
						<div
							key={code}
							style={{
								width: px(72),
								height: px(72),
								borderRadius: "50%",
								overflow: "hidden",
								border: `${px(3)} solid #FFFFFF`,
								boxShadow: `0 ${px(2)} ${px(8)} rgba(15,62,74,0.18)`,
								// They arrive one after the other, left to right.
								opacity: interpolate(frame, [NAME_IN + i * 4, NAME_IN + i * 4 + 10], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" }),
							}}
						>
							<Img src={`https://flagcdn.com/w160/${code}.png`} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
						</div>
					))}
				</div>
			) : null}
			<span style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600, fontSize: px(56), color: "#0F3E4A" }}>{tripName}</span>
			{stats ? <span style={{ fontFamily: "Poppins, sans-serif", fontWeight: 500, fontSize: px(30), color: "#0F3E4A", opacity: 0.5 }}>{stats}</span> : null}
		</div>
	) : null;

	return (
		<AbsoluteFill style={{ opacity: sheet, backgroundColor: "#FFFFFF", zIndex: 100 }}>
			<AbsoluteFill
				style={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					gap: px(landscape ? 40 : 48),
					paddingBottom: px(landscape ? 150 : 430),
				}}
			>
				{landscape ? (
					<>
						{/* The drawing on top, then the signature and the trip sharing one line beneath it.
						    Stacking all three in a wide frame leaves it mostly margin. */}
						{routeBlock}
						<div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", gap: px(110) }}>
							{lockup}
							{tripInfo}
						</div>
					</>
				) : (
					<>
						{/* Which trip it was, then its shape, then whose it is. The name led the drawing
						    rather than following it once the card was read as a whole -- a route you cannot
						    place yet is a squiggle. */}
						{tripInfo}
						{routeBlock}
						{lockup}
					</>
				)}
			</AbsoluteFill>

			{/* The store buttons sit clear of the bottom edge; the map credit stays on it.
			    They were one column, so raising the buttons dragged the credit up with them -- and the
			    credit belongs at the very bottom, which is the whole point of moving it there. */}
			<AbsoluteFill
				style={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "flex-end",
					paddingBottom: px(landscape ? 120 : 470),
				}}
			>
				{partner ? null : (
					<div
						style={{
							display: "flex",
							flexDirection: "row",
							gap: px(24),
							opacity: interpolate(badgeIn, [0, 1], [0, 1], { extrapolateRight: "clamp" }),
							transform: `translateY(${interpolate(badgeIn, [0, 1], [22, 0]) * scale}px)`,
						}}
					>
						<Img src={AppStore} alt="App Store" style={{ height: px(84), width: "auto" }} />
						<Img src={PlayStore} alt="Google Play" style={{ height: px(84), width: "auto" }} />
					</div>
				)}
			</AbsoluteFill>

			<AbsoluteFill
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "flex-end",
					paddingBottom: px(landscape ? 34 : 74),
				}}
			>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						gap: px(9),
						opacity: interpolate(frame, [ATTRIB_IN, ATTRIB_IN + 12], [0, 0.45], { extrapolateLeft: "clamp", extrapolateRight: "clamp" }),
					}}
				>
					<span
						style={{
							fontFamily: "Poppins, sans-serif",
							fontWeight: 500,
							fontSize: px(17),
							letterSpacing: px(1.2),
							color: "#0F3E4A",
							textTransform: "uppercase",
						}}
					>
						Maps by
					</span>
					<Img src={MapboxLogo} alt="Mapbox" style={{ height: px(24), width: "auto" }} />
				</div>
			</AbsoluteFill>

			{brandName ? (
				<AbsoluteFill style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", paddingBottom: px(120) }}>
					{/* The partner's logo is the signature above; this is the quiet credit underneath it.
					    It used to read "powered by {brandName}", which on a partner's own card said they
					    were powered by themselves. */}
					<span style={{ fontFamily: "Poppins, sans-serif", fontSize: px(22), color: "#0F3E4A", opacity: 0.45 }}>powered by Stippl</span>
				</AbsoluteFill>
			) : null}
		</AbsoluteFill>
	);
};

export default ReelOutro;
